import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";

import Flag from "react-flagkit";

import {
  Language,
  TranslateContextData,
  TranslateProviderProps,
} from "./interfaces";
import content from "../support/languages/index.json";
import { ITypesLanguages } from "../common/interfaces";
import { getLanguage, setLanguage } from "../support/storage/BrowserStorage";
import { get, has } from "lodash";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import ptBrLocale from "date-fns/locale/pt-BR";
import enUSLocale from "date-fns/locale/en-US";
import esLocale from "date-fns/locale/es";

const TranslateContext = createContext<TranslateContextData>(
  {} as TranslateContextData
);

const languages: Language[] = [
  {
    language: "EN",
    label: "EN",
    code: "en-us",
    flag: <Flag size={15} country="US" />,
    isDefault: false,
  },
  {
    language: "PT",
    label: "PT",
    code: "pt-br",
    flag: <Flag size={15} country="BR" />,
    isDefault: true,
  },
  {
    language: "ES",
    label: "ES",
    code: "es-es",
    flag: <Flag size={15} country="ES" />,
    isDefault: false,
  },
];

export function TranslateProvider({ children }: TranslateProviderProps) {
  const [currentLanguage, setCurrentLanguage] = useState<Language>();

  const currentContent = useMemo(() => {
    if (currentLanguage) return content[currentLanguage.language];
  }, [currentLanguage]);

  const translate = useCallback(
    (text: string) => {
      if (!currentContent) return "";
      if (!has(currentContent, text)) return text;
      return get(currentContent, text) ?? text;
    },
    [currentContent]
  );

  const handleChooseLanguage = (language: ITypesLanguages) => {
    const foundLanguage = languages.find((item) => item.language === language);

    if (foundLanguage == null) return;

    setLanguage(language);
    setCurrentLanguage(foundLanguage);
  };

  useEffect(() => {
    const storageLanguage = getLanguage();

    if (storageLanguage) {
      handleChooseLanguage(storageLanguage as ITypesLanguages);
      return;
    }

    const language = languages.find((item) => !!item.isDefault);
    handleChooseLanguage((language?.language as ITypesLanguages) || "PT");
  }, []);

  const adapterLocale = useMemo(() => {
    switch (currentLanguage?.code) {
      case "en-us":
        return enUSLocale;
      case "pt-br":
        return ptBrLocale;
      case "es-es":
        return esLocale;
    }
    return ptBrLocale;
  }, [currentLanguage]);

  const contextProps = useMemo(
    () => ({
      translate,
      languages,
      currentLanguage: currentLanguage!,
      handleChooseLanguage,
    }),
    [currentLanguage, translate]
  );

  if (!currentLanguage) return null;

  return (
    <TranslateContext.Provider value={contextProps}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={adapterLocale}
      >
        {children}
      </LocalizationProvider>
    </TranslateContext.Provider>
  );
}

export const useTranslate = () => useContext(TranslateContext);
